// Sidebar.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Image } from "react-bootstrap";
import SidebarLogo from "../../assets/images/sidebar-icon/mep-sidebar-logo.png";
import "./sidebar.css";
import { SidebarContent } from "./sidebar-content";
import { routes } from "../../routes/pageRoutes";

// Helper: check if a path matches current location
const matchPath = (locationPath, testPath) => {
  if (!testPath) return false;

  // normalize (remove trailing slashes; keep root as '/')
  const norm = p => (p.replace(/\/+$/, '') || '/');
  const loc = norm(locationPath);
  const test = norm(testPath);

  // exact for root
  if (test === '/') return loc === '/';

  // handle param routes like /users/:id
  if (test.includes(':')) {
    const rx = new RegExp('^' + test.replace(/:[^/]+/g, '[^/]+') + '$');
    return rx.test(loc);
  }

  // exact or path-boundary prefix (/npa matches /npa/..., but not /npaX)
  return loc === test || loc.startsWith(test + '/');
};

// Helper: determine active link anywhere in a nested tree
const treeHasActiveLink = (node, locationPath) => {
  if (node.link && matchPath(locationPath, node.link)) return true;
  if (Array.isArray(node.children)) {
    return node.children.some(child => treeHasActiveLink(child, locationPath));
  }
  return false;
};

function SidebarNode({ node, level, locationPath }) {
  const [open, setOpen] = useState(false);

  // Auto-open any node that contains the active route
  const isActiveInTree = useMemo(() => treeHasActiveLink(node, locationPath), [node, locationPath]);
  useEffect(() => {
    if (isActiveInTree) setOpen(true);
  }, [isActiveInTree]);

  const isLeaf = !node.children || node.children.length === 0;

  // Spacing by level
  const padLeft = 12 + level * 14;

  if (isLeaf) {
    const active = node.link && matchPath(locationPath, node.link);
    return (
      <li className="sidebar-item">
        <Link
          to={node.link || "#"}
          className={active ? "active d-flex align-items-center" : "d-flex align-items-center"}
          style={{ paddingLeft: padLeft }}
        >
          {node.icon && <span className="me-2 d-inline-flex">{node.icon}</span>}
          <span className="sidebar-title-mr">{node.title}</span>
        </Link>
      </li>
    );
  }

  // Group / dropdown
  // Group / dropdown
return (
  <li className={`sidebar-item ${open ? 'open' : ''}`}>
    <button
      type="button"
      onClick={() => setOpen(o => !o)}
      className={`w-100 d-flex align-items-center btn btn-link text-decoration-none ${
        isActiveInTree ? 'active' : ''
      }`}
      style={{ paddingLeft: padLeft }}
    >
      {node.icon && <span className="me-2 d-inline-flex">{node.icon}</span>}
      <span className="flex-grow-1 text-start">{node.title}</span>
      <span className={`rotate-icon ${open ? 'rotate' : ''}`}>
        {node.dropdownIcon}
      </span>
    </button>

    <ul className="dropdown-content list-unstyled">
      {node.children.map((child, idx) => (
        <SidebarNode
          key={idx}
          node={child}
          level={level + 1}
          locationPath={locationPath}
        />
      ))}
    </ul>
  </li>
);

}

export default function Sidebar({ className }) {
  const [scrolled, setScrolled] = useState(false);
  const sidebarRef = useRef(null);
  const location = useLocation();

  const handleSidebarScroll = () => {
    const scrollPosition = sidebarRef.current?.scrollTop || 0;
    setScrolled(scrollPosition > 1);
  };

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    sidebar.addEventListener("scroll", handleSidebarScroll);
    return () => sidebar.removeEventListener("scroll", handleSidebarScroll);
  }, []);

  return (
    <aside className={`bg-white ${className} sidebar`}>
      <div className="h-100 position-relative d-flex flex-column">
        <div className="flex-shrik-1 position-relative mt-3 mb-2">
          <div className={`d-flex align-items-center position-sticky top-0 start-0 end-0 site-logo ${scrolled ? "effect-logo" : ""}`}>
            <Link to={routes.home}>
              <h1><Image src={SidebarLogo} /></h1>
            </Link>
          </div>
        </div>

        <div className="py-1 flex-grow-1 overflow-y-auto" ref={sidebarRef}>
          <ul className="mb-0 list-unstyled">
            {SidebarContent.map((node, i) => (
              <SidebarNode key={i} node={node} level={0} locationPath={location.pathname} />
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

